/**
 * Reusable background-style chunked fetching and row processing utilities for full-database exports.
 *
 * Supabase PostgREST enforces `db-max-rows = 2000` per HTTP request on all TABLE-returning RPCs
 * and `.from(...).select(...)` queries (and some SQL functions clamp limits even lower, e.g. 250).
 * This utility loops in safe batches, yields to the browser UI event loop between batches so the
 * progress bar renders smoothly without freezing the main thread, and returns 100% of matching rows.
 */

export interface FetchAllInBatchesOptions<T> {
  /**
   * Function that fetches a single batch starting at `offset` (0-based) with size `limit` and 1-based `page`.
   */
  fetchBatch: (
    offset: number,
    limit: number,
    page: number
  ) => Promise<{
    rows: T[];
    total?: number | null;
  }>;
  /**
   * Number of rows to request per batch. Must be <= PostgREST max_rows (2000). Default: 1000.
   */
  batchSize?: number;
  /**
   * Known total record count from the table state (used for accurate initial progress calculation).
   */
  knownTotal?: number | null;
  /**
   * Progress callback invoked after each batch is fetched.
   * @param fetched Total rows accumulated so far
   * @param total Total expected rows (if known)
   * @param percent Progress percentage mapped to the fetching phase (5% - 85%)
   */
  onProgress?: (
    fetched: number,
    total: number | null,
    percent: number
  ) => void;
  /**
   * Safety cap on maximum batches to prevent infinite loops if an endpoint misbehaves. Default: 500.
   */
  maxBatches?: number;
}

/**
 * Yields control back to the browser event loop so React state updates and progress bar animations paint smoothly.
 */
export const yieldToMainThread = (ms = 20): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export async function fetchAllInBatches<T>({
  fetchBatch,
  batchSize = 1000,
  knownTotal = null,
  onProgress,
  maxBatches = 500,
}: FetchAllInBatchesOptions<T>): Promise<T[]> {
  const accumulated: T[] = [];
  let offset = 0;
  let page = 1;
  let expectedTotal: number | null =
    typeof knownTotal === "number" && knownTotal > 0 ? knownTotal : null;

  if (onProgress) {
    onProgress(0, expectedTotal, 5);
  }
  await yieldToMainThread(15);

  for (let batchIndex = 0; batchIndex < maxBatches; batchIndex++) {
    const { rows, total } = await fetchBatch(offset, batchSize, page);
    const batchRows = Array.isArray(rows) ? rows : [];

    if (typeof total === "number" && total > 0) {
      expectedTotal = total;
    }

    if (batchRows.length === 0) {
      break;
    }

    accumulated.push(...batchRows);

    const fetchedCount = accumulated.length;
    if (expectedTotal && expectedTotal < fetchedCount) {
      expectedTotal = fetchedCount;
    }

    const percent = expectedTotal
      ? Math.min(85, Math.max(8, Math.round((fetchedCount / expectedTotal) * 85)))
      : Math.min(80, 10 + batchIndex * 10);

    if (onProgress) {
      onProgress(fetchedCount, expectedTotal, percent);
    }

    // Yield to browser UI thread so progress bar updates smoothly
    await yieldToMainThread(20);

    // Stop if we received fewer rows than requested OR we have reached the expected total count
    if (batchRows.length < batchSize) {
      break;
    }
    if (expectedTotal && fetchedCount >= expectedTotal) {
      break;
    }

    offset += batchRows.length;
    page += 1;
  }

  return accumulated;
}

/**
 * Maps a large array in chunks on the main thread while updating progress from `startPercent` to `endPercent`,
 * preventing UI freezes when formatting thousands of rows for PDF or Excel.
 */
export async function mapRowsInChunks<T, R>(
  items: T[],
  mapper: (item: T, index: number) => R,
  options?: {
    chunkSize?: number;
    startPercent?: number;
    endPercent?: number;
    onProgress?: (processed: number, total: number, percent: number) => void;
  }
): Promise<R[]> {
  const total = items.length;
  const chunkSize = options?.chunkSize ?? 1000;
  const startPercent = options?.startPercent ?? 85;
  const endPercent = options?.endPercent ?? 95;
  const result: R[] = new Array(total);

  for (let i = 0; i < total; i += chunkSize) {
    const end = Math.min(i + chunkSize, total);
    for (let j = i; j < end; j++) {
      result[j] = mapper(items[j], j);
    }

    if (options?.onProgress && total > 0) {
      const ratio = end / total;
      const percent = Math.round(
        startPercent + ratio * (endPercent - startPercent)
      );
      options.onProgress(end, total, percent);
    }

    if (end < total) {
      await yieldToMainThread(10);
    }
  }

  return result;
}

